import { useEffect, useMemo, type FC } from 'react'
import { useForm, Controller } from 'react-hook-form'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import {
  type EnrollmentHYCSEnterPhoneSubmitInput,
  useGetEnrollmentHYCSEnterPhoneData,
  useSubmitEnrollmentHYCSEnterPhoneData,
} from '@egw/api'
import { Input, Button, Alert, AlertType, GenericError } from '@egw/components'
import { useTranslation } from '@egw/i18n'
import { Logger } from '@egw/logger'
import { EnrollmentHYCSLayout } from '@egw/layouts'
import { useRouter } from '@egw/router'
import { sendEvent } from '@egw/events'
import { passkeyService } from '@egw/auth'
import { WEALTH_ROUTES } from '@egw/constants'
import { testUSPhoneNumber } from '@egw/utils'
import { PageHeader } from '../../../components'
import { useValidateRoute } from '../../../hooks/navigation/validate-route'
import { formatPhoneNumberWwithMask, preformatPhoneNumber } from '../../../utils/phone-number/phone-number'
import { EDataLayerEventWealth } from '../../../constants/events/EDataLayerEventWealth'
import { newPathIsDifferent } from '../../../utils/general/new-path-is-different'
import { ROUTES } from '../../../constants/routes/routes-generic'

const log = new Logger('Enrollment - HYCS - EnterPhone')

interface FormData {
  phoneNumber: string
}

export const EnterPhonePage: FC = () => {
  const { t } = useTranslation('features-enrollment')
  const { navigate } = useRouter()

  const isMobile = passkeyService.getIsMobile()

  const { data, isLoading, error } = useGetEnrollmentHYCSEnterPhoneData()
  const { mutate, isLoading: isSubmitting, error: submitError } = useSubmitEnrollmentHYCSEnterPhoneData()

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors: formErrors, isValid },
  } = useForm({
    defaultValues: {
      phoneNumber: '',
    },
    mode: 'onBlur',
  })

  const phoneNumber = watch('phoneNumber')

  const variant = useMemo(() => {
    if (isMobile) {
      return 'link'
    }
    return 'otp'
  }, [isMobile])

  useEffect(() => {
    if (preformatPhoneNumber(phoneNumber).length === 1) {
      sendEvent(EDataLayerEventWealth.BANKHYCHYS_PHONE_INTENT)
    }
  }, [phoneNumber])

  // Protect route
  useValidateRoute(data?.path)

  const onSubmit = handleSubmit(async (fData: unknown): Promise<void> => {
    const formData = fData as FormData
    if (isValid) {
      // Get payload
      let payload: EnrollmentHYCSEnterPhoneSubmitInput
      if (isMobile) {
        payload = {
          triggerPhoneMagicLink: true,
          phoneNumber: preformatPhoneNumber(formData.phoneNumber),
          phoneCountryCode: '1',
        }
      } else {
        payload = {
          triggerPhoneOtp: true,
          phoneNumber: preformatPhoneNumber(formData.phoneNumber),
          phoneCountryCode: '1',
        }
      }

      try {
        sendEvent(EDataLayerEventWealth.BANKHYCHYS_PHONE_SUBMIT)
        const response = await mutate(payload)
        // If path exists and is different
        if (response?.path && newPathIsDifferent(location.pathname, response.path)) {
          navigate(response.path)
        } else {
          // If no path from BE, redirect to error
          if (!response?.path) {
            navigate(`${WEALTH_ROUTES.ENROLLMENT.path}/${ROUTES.ERROR.path}`)
          }
        }
      } catch (e) {
        log.error('Error submitting form', e)
      }
    }
  })

  return (
    <EnrollmentHYCSLayout title={t(`hycs.enter_phone.${variant}.page_title`)}>
      <div className="egw-container-enroll-hycs h-full flex flex-col relative md:justify-center justify-start items-start">
        {/* Loading initial data to make sure the user has access to this screen and retreive data based on user enrollment application */}
        {isLoading ? (
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={1} height="100px" className="mb-10" />
            <Skeleton count={5} height="50px" />
          </SkeletonTheme>
        ) : null}

        {/* In some cases initial data can be null, so we assume if it's not loading and there's no error - we show the page */}
        {!isLoading && !error ? (
          <form
            onSubmit={(e) => void onSubmit(e)}
            className="w-full flex-1 flex flex-col justify-start lg:justify-center"
          >
            <PageHeader title={t(`hycs.enter_phone.${variant}.title`)} className="mb-md" />
            <p className="typo-primary-base-regular text-text-meta mb-xl">
              {t(`hycs.enter_phone.${variant}.description`)}
            </p>

            <Controller
              name="phoneNumber"
              control={control}
              rules={{
                required: t('common.required_field'),
                pattern: {
                  value: /^\+1 \(\d{3}\) \d{3}-\d{4}$/i,
                  message: t(`hycs.enter_phone.${variant}.invalid_phone_number`),
                },
              }}
              render={({ field }) => (
                <Input
                  {...field}
                  label={t(`hycs.enter_phone.${variant}.phone_number`)}
                  onInput={(e) => {
                    formatPhoneNumberWwithMask(e)
                  }}
                  required
                  testId="phoneNumber_input"
                  error={formErrors.phoneNumber?.message}
                />
              )}
            />

            <div className="flex-1 md:hidden" />

            {submitError ? <Alert alertType={AlertType.Error} description={t('common.generic_error')} className="mt-sm" /> : null}

            {/* AUTO GENERATED COMPONENTS */}

            <Button
              type="submit"
              className="w-full md:w-min mt-xl"
              isDisabled={!isValid}
              isLoading={isSubmitting}
              testId="primaryAction_button"
            >
              {t('common.continue')}
            </Button>
          </form>
        ) : null}

        {/* This error handles the case when the initial data is not loaded, for example network error */}
        {error ? (
          <div className="flex-1 flex flex-col justify-start md:justify-center">
            <GenericError errorMessage={t('common.generic_error')} />
          </div>
        ) : null}
      </div>
    </EnrollmentHYCSLayout>
  )
}
